import type { PurchaseListItemInput, CostSource, PurchaseListItemInventory } from '@/types/purchase-list';

interface SupplierLike {
  id: number | string;
  name?: string;
  currency_code?: string;
  currency?: string;
}

const FIELD_CANDIDATES = {
  variantId:      ['variantId', 'variant_id'],
  productName:    ['Item[Item Description]', 'productName', 'product_name'],
  variant:        ['variant'],
  sku:            ['sku', 'product_sku'],
  supplierSku:    ['supplierSku', 'supplier_sku'],
  variantImage:   ['variantImage', 'variant_image'],
  supplierId:     ['supplierId', 'supplier_id'],
  supplierName:   ['supplierName', 'supplier_name', 'supplier'],
  unitCost:       ['cost', 'unitPrice', '[average_unit_cost]', '[unit_cost]', '[replenishment_inventory_cost]'],
  qtyCandidates:  ['reorderQuantity', '[inventory_replenishment]', 'suggestedQuantity', 'suggested_quantity'],
  currencyRow:    ['currencyCode', 'currency_code'],
  invStock:       ['[Actual Inventory Quantity]', 'stockOnHand', 'stock_on_hand'],
  invIncoming:    ['[incoming_inventory_quantity]', 'incomingQuantity', 'incoming_quantity'],
  invDaysLeft:    ['inventoryRemainingDays', '[stock_days_inventory_planning]', 'daysLeft'],
  invSuggested:   ['[inventory_replenishment]', 'suggestedQuantity', 'suggested_quantity', 'reorderQuantity'],
};

function firstDefined<T = unknown>(row: any, keys: string[]): T | undefined {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && row?.[k] !== '') { return row[k] as T; }
  }
  return undefined;
}

function toFiniteNumberOrNull(raw: unknown): number | null {
  if (raw === null || raw === undefined || raw === 'unknown') { return null; }
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function resolveInventory(row: any): PurchaseListItemInventory | undefined {
  const stock    = toFiniteNumberOrNull(firstDefined(row, FIELD_CANDIDATES.invStock));
  const incoming = toFiniteNumberOrNull(firstDefined(row, FIELD_CANDIDATES.invIncoming));
  const daysLeft = toFiniteNumberOrNull(firstDefined(row, FIELD_CANDIDATES.invDaysLeft));
  const suggested = toFiniteNumberOrNull(firstDefined(row, FIELD_CANDIDATES.invSuggested));
  // If every signal is null, return undefined so we don't attach an empty
  // metadata.inventory object to rows that clearly had no inventory data.
  if (stock === null && incoming === null && daysLeft === null && suggested === null) {
    return undefined;
  }
  return { stock, incoming, daysLeft, suggested };
}

export function rowToPurchaseListItem(row: any, suppliers: SupplierLike[] = []): PurchaseListItemInput {
  const rawSupplierId = firstDefined<number | string>(row, FIELD_CANDIDATES.supplierId);
  const isUnknownSupplier = rawSupplierId === 'unknown' || rawSupplierId == null;
  const supplierId = isUnknownSupplier ? null : Number(rawSupplierId);
  const supplierRecord = supplierId != null
    ? suppliers.find((s) => Number((s as any).id) === supplierId)
    : undefined;

  const qtyRaw = [...FIELD_CANDIDATES.qtyCandidates]
    .map((k) => Number(row?.[k]))
    .find((n) => Number.isFinite(n) && n > 0);
  const quantity = qtyRaw ?? 1;

  const currencyCode = (supplierRecord?.currency_code
    ?? supplierRecord?.currency
    ?? firstDefined<string>(row, FIELD_CANDIDATES.currencyRow)
    ?? 'USD') as string;

  const unitCostRaw = firstDefined<number | string>(row, FIELD_CANDIDATES.unitCost) ?? 0;
  const unitCost = Number(unitCostRaw) || 0;

  // US-CURR-013: every candidate above is a Shopify/OLAP report field
  // ([average_unit_cost], [unit_cost], ...), so this cost is in the STORE currency —
  // never the supplier's, regardless of whether a supplier is assigned. Declaring
  // that honestly lets PurchaseListService resolve it (SupplierProduct.cost, else
  // convert by the FX rate). Tagging it 'supplier' previously claimed it was already
  // in supplier currency; tagging unassigned rows 'manual' claimed the user typed it
  // and froze the value even after a supplier was assigned.
  const costSource: CostSource = 'shopify';

  const inventory = resolveInventory(row);

  return {
    variantId: toFiniteNumberOrNull(firstDefined(row, FIELD_CANDIDATES.variantId)) as any,
    productName: (firstDefined<string>(row, FIELD_CANDIDATES.productName) ?? '') as string,
    variant: (firstDefined<string>(row, FIELD_CANDIDATES.variant) ?? null) as string | null,
    sku: (firstDefined<string>(row, FIELD_CANDIDATES.sku) ?? null) as string | null,
    supplierSku: (firstDefined<string>(row, FIELD_CANDIDATES.supplierSku) ?? null) as string | null,
    variantImage: (firstDefined<string>(row, FIELD_CANDIDATES.variantImage) ?? null) as string | null,
    supplierId,
    supplierName: isUnknownSupplier
      ? null
      : (supplierRecord?.name ?? (firstDefined<string>(row, FIELD_CANDIDATES.supplierName) ?? null)),
    quantity,
    unitCost,
    costSource,
    currencyCode,
    ...(inventory ? { metadata: { inventory } } : {}),
  };
}
